import {
  Banner,
  Button,
  Card,
  CardBody,
  Drawer,
  SelectInput,
  Tag,
  Text,
  TextInput,
  TOKENS,
} from "@bestowinc/kindred-ds";
import type { ReactNode } from "@bestowinc/tinkertoy-shell-deps/react";
import {
  useCallback,
  useState,
} from "@bestowinc/tinkertoy-shell-deps/react";
import styled from "@bestowinc/tinkertoy-shell-deps/styled-components";

import type { DevBundleOverrides } from "../runtime-config/read-shell-config";
import { useShellRuntime } from "../shell-runtime";
import {
  clearBundleOverrides,
  deleteBundleOverride,
  fetchDevToolsSnapshot,
  putBundleOverride,
  putManifestVersionOverride,
  putShellVersionOverride,
  type DevToolsMutationResponse,
  type DevToolsSnapshot,
} from "./dev-tools-api";
import type { LoadedModuleVersionRow } from "./use-loaded-version-rows";
import {
  useDevToolsPanelState,
  useLoadedVersionRows,
} from "./use-loaded-version-rows";
import type { DeveloperToolsConfig, ManifestVersionOverride } from "./types";

// Keep the toggle above enrollment overlays (Kindred Drawer overlay is z-index
// 999; carrier modals and sticky chrome can stack higher). Use a value just
// below INT32_MAX so the fixed button stays reachable without fighting other
// near-max layers (some third-party widgets use 2147483647).
const DEV_TOOLS_Z_INDEX = 2_147_483_640;

const ToggleWrap = styled.div`
  position: fixed;
  bottom: var(${TOKENS.dimension.space["4"]});
  right: var(${TOKENS.dimension.space["4"]});
  z-index: ${DEV_TOOLS_Z_INDEX};
`;

const ToggleButtonWrap = styled.div`
  position: relative;
`;

const OverrideIndicatorDot = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 8px;
  height: 8px;
  border-radius: var(${TOKENS.dimension.radius.surfaceRound});
  background-color: var(${TOKENS.color.background.feedback.warningStrong});
  pointer-events: none;
`;

const Section = styled.section`
  & + & {
    margin-top: var(${TOKENS.dimension.space["5"]});
    padding-top: var(${TOKENS.dimension.space["5"]});
    border-top: var(${TOKENS.dimension.borderWidth.division}) solid
      var(${TOKENS.color.border.default});
  }
`;

const SectionHeading = styled.h3`
  margin: 0 0 var(${TOKENS.dimension.space["2"]});
  font: inherit;
`;

const DevToolsCardBody = styled(CardBody)`
  display: flex;
  flex-direction: column;
  gap: var(${TOKENS.dimension.space["2"]});

  p {
    margin: 0;
  }
`;

const Mono = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  overflow-wrap: anywhere;
`;

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(${TOKENS.dimension.space["2"]});
`;

const ItemListItem = styled.li`
  list-style: none;
`;

const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(${TOKENS.dimension.space["2"]});
`;

const RemoveButtonWrap = styled.div`
  flex-shrink: 0;
`;

const KindTag = styled(Tag)`
  margin-right: var(${TOKENS.dimension.space["1"]});
  vertical-align: middle;
  cursor: default;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(${TOKENS.dimension.space["2"]});
  padding-top: var(${TOKENS.dimension.space["2"]});
  border-top: var(${TOKENS.dimension.borderWidth.division}) solid
    var(${TOKENS.color.border.subtle});
`;

const MetaField = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(${TOKENS.dimension.space["1"]});
  min-width: 0;
`;

const FieldLabel = styled.span`
  color: var(${TOKENS.color.foreground.subdued});
`;

const CardDetailSection = styled.div`
  padding-top: var(${TOKENS.dimension.space["2"]});
  border-top: var(${TOKENS.dimension.borderWidth.division}) solid
    var(${TOKENS.color.border.subtle});
`;

const FormDivider = styled.div`
  margin-top: var(${TOKENS.dimension.space["4"]});
  padding-top: var(${TOKENS.dimension.space["4"]});
  border-top: var(${TOKENS.dimension.borderWidth.division}) solid
    var(${TOKENS.color.border.default});
  display: grid;
  gap: var(${TOKENS.dimension.space["3"]});
`;

const SectionCopy = styled.div`
  margin-top: var(${TOKENS.dimension.space["2"]});
`;

const FieldStack = styled.div`
  display: grid;
  gap: var(${TOKENS.dimension.space["3"]});
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(${TOKENS.dimension.space["2"]});
  margin-top: var(${TOKENS.dimension.space["3"]});
`;

function MetaFieldLabel({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <MetaField>
      <FieldLabel>
        <Text variant="labelSmall" element="span">
          {label}
        </Text>
      </FieldLabel>
      <Text variant="bodySmall" element="span">
        {children}
      </Text>
    </MetaField>
  );
}

function LoadedModuleItem({ row }: { row: LoadedModuleVersionRow }) {
  return (
    <ItemListItem>
      <Card data-testid="loaded-module-item">
        <DevToolsCardBody>
          <Text variant="labelMedium" element="p">
            <Mono>{row.moduleId}</Mono>
          </Text>
          <MetaGrid>
            <MetaFieldLabel label="Manifest">
              <Mono>{row.manifestVersion ?? "—"}</Mono>
            </MetaFieldLabel>
            <MetaFieldLabel label="Resolved">
              <Mono>{row.resolvedVersion}</Mono>
            </MetaFieldLabel>
          </MetaGrid>
          {row.isOverridden && row.overrideBundleUrl ? (
            <CardDetailSection>
              <MetaFieldLabel label="Override">
                <Mono>{row.overrideBundleUrl}</Mono>
              </MetaFieldLabel>
            </CardDetailSection>
          ) : null}
        </DevToolsCardBody>
      </Card>
    </ItemListItem>
  );
}

function BundleOverrideItem({
  kind,
  id,
  bundleUrl,
  onRemove,
}: {
  kind: "view" | "module";
  id: string;
  bundleUrl: string;
  onRemove: () => void;
}) {
  return (
    <ItemListItem>
      <Card data-testid="bundle-override-item">
        <DevToolsCardBody>
          <CardTitleRow>
            <Text variant="bodySmall" element="p">
              <KindTag variant="gray" hideIcon>
                {kind}
              </KindTag>
              <Mono>{id}</Mono>
            </Text>
            <RemoveButtonWrap>
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={onRemove}
              >
                Remove
              </Button>
            </RemoveButtonWrap>
          </CardTitleRow>
          <MetaFieldLabel label="Bundle URL">
            <Mono>{bundleUrl}</Mono>
          </MetaFieldLabel>
        </DevToolsCardBody>
      </Card>
    </ItemListItem>
  );
}

function BundleOverrideList({
  snapshot,
  onRemove,
}: {
  snapshot: DevToolsSnapshot;
  onRemove: (kind: "view" | "module", id: string) => void;
}) {
  const viewRows = Object.entries(snapshot.bundleOverrides.views ?? {});
  const moduleRows = Object.entries(snapshot.bundleOverrides.modules ?? {});

  if (viewRows.length === 0 && moduleRows.length === 0) {
    return <Text variant="bodySmall">No bundle overrides active.</Text>;
  }

  return (
    <ItemList>
      {viewRows.map(([id, entry]) => (
        <BundleOverrideItem
          key={`view:${id}`}
          kind="view"
          id={id}
          bundleUrl={entry.bundleUrl}
          onRemove={() => onRemove("view", id)}
        />
      ))}
      {moduleRows.map(([id, entry]) => (
        <BundleOverrideItem
          key={`module:${id}`}
          kind="module"
          id={id}
          bundleUrl={entry.bundleUrl}
          onRemove={() => onRemove("module", id)}
        />
      ))}
    </ItemList>
  );
}

function AddOverrideForm({
  onSubmit,
}: {
  onSubmit: (values: {
    kind: "view" | "module";
    id: string;
    bundleUrl: string;
    manifestUrl: string;
  }) => void;
}) {
  const [kind, setKind] = useState<"view" | "module">("module");
  const [id, setId] = useState("");
  const [bundleUrl, setBundleUrl] = useState("");
  const [manifestUrl, setManifestUrl] = useState("");

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({ kind, id, bundleUrl, manifestUrl });
      }}
    >
      <FieldStack>
        <SelectInput
          id="dev-tools-override-kind"
          label="Kind"
          value={kind}
          onChange={(event) =>
            setKind(event.target.value === "view" ? "view" : "module")
          }
        >
          <option value="module">module</option>
          <option value="view">view</option>
        </SelectInput>
        <TextInput
          id="dev-tools-override-id"
          label="ID"
          value={id}
          onChange={(event) => setId(event.target.value)}
        />
        <TextInput
          id="dev-tools-override-bundle-url"
          label="Bundle URL"
          value={bundleUrl}
          onChange={(event) => setBundleUrl(event.target.value)}
          placeholder="http://localhost:5173/modules/.../dev.js"
        />
        {kind === "view" ? (
          <TextInput
            id="dev-tools-override-manifest-url"
            label="Manifest URL (optional)"
            value={manifestUrl}
            onChange={(event) => setManifestUrl(event.target.value)}
            placeholder="http://localhost:5173/views/.../manifest.json"
          />
        ) : null}
        <Button type="submit" size="small">
          Save bundle override
        </Button>
      </FieldStack>
    </form>
  );
}

function hasBundleOverrideEntries(
  overrides: DevBundleOverrides | undefined,
): boolean {
  return (
    Object.keys(overrides?.views ?? {}).length > 0 ||
    Object.keys(overrides?.modules ?? {}).length > 0
  );
}

async function clearAllOverrides(
  apiBaseUrl: string,
  manifestVersionOverride: ManifestVersionOverride | null,
): Promise<DevToolsMutationResponse> {
  const bundleResult = await clearBundleOverrides(apiBaseUrl);
  if (manifestVersionOverride === null) {
    return bundleResult;
  }
  return putManifestVersionOverride(
    apiBaseUrl,
    manifestVersionOverride.branchId,
    null,
  );
}

function ManifestVersionOverrideForm({
  onSubmit,
}: {
  onSubmit: (values: { branchId: string; version: string }) => void;
}) {
  const [branchId, setBranchId] = useState("");
  const [version, setVersion] = useState("");

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (branchId === "" || version === "") {
          return;
        }
        onSubmit({ branchId, version });
      }}
    >
      <FieldStack>
        <TextInput
          id="dev-tools-manifest-branch-id"
          label="Branch ID"
          value={branchId}
          onChange={(event) => setBranchId(event.target.value)}
        />
        <TextInput
          id="dev-tools-manifest-version"
          label="Manifest version"
          value={version}
          onChange={(event) => setVersion(event.target.value)}
          placeholder="e.g. 1.4.0"
        />
        <Button type="submit" size="small">
          Set manifest version override
        </Button>
      </FieldStack>
    </form>
  );
}

type DeveloperToolsDrawerBodyProps = {
  apiBaseUrl: string;
  developerTools: DeveloperToolsConfig;
  snapshot: DevToolsSnapshot | null;
  isLoading: boolean;
  errorMessage: string | null;
  statusMessage: string | null;
  pendingReload: boolean;
  onReloadPage: () => void;
  onApplyMutation: (
    promise: Promise<{ message: string; reloadRequired: boolean }>,
  ) => Promise<void>;
};

function DeveloperToolsDrawerBody({
  apiBaseUrl,
  developerTools,
  snapshot,
  isLoading,
  errorMessage,
  statusMessage,
  pendingReload,
  onReloadPage,
  onApplyMutation,
}: DeveloperToolsDrawerBodyProps) {
  const { viewId, viewVersion, moduleRows } = useLoadedVersionRows();

  const shellVersion = snapshot?.shellVersion ?? developerTools.shellVersion;
  const shellVersionOverride =
    snapshot?.shellVersionOverride ?? developerTools.shellVersionOverride;
  const shellOverrideActive =
    shellVersionOverride !== null &&
    shellVersionOverride !== developerTools.defaultShellVersion;

  const manifestVersionOverride =
    snapshot !== null
      ? snapshot.manifestVersionOverride
      : developerTools.manifestVersionOverride;

  return (
    <>
      <Card>
        <DevToolsCardBody>
          <Text variant="bodySmall" element="p">
            <FieldLabel>
              <Text variant="labelSmall" element="span">
                Shell
              </Text>
            </FieldLabel>
            {" "}
            <Mono>{shellVersion}</Mono>
            {shellOverrideActive ? (
              <>
                {" "}
                <Text variant="bodySmall" element="span">
                  (override: <Mono>{shellVersionOverride}</Mono>)
                </Text>
              </>
            ) : null}
          </Text>
          <Text variant="bodySmall" element="p">
            <FieldLabel>
              <Text variant="labelSmall" element="span">
                View
              </Text>
            </FieldLabel>
            {" "}
            {viewId !== undefined && viewVersion !== undefined ? (
              <Mono>
                {viewId} @ {viewVersion}
              </Mono>
            ) : (
              <Text variant="bodySmall" element="span">
                Unavailable — loader did not return view metadata.
              </Text>
            )}
          </Text>
        </DevToolsCardBody>
      </Card>

      <Section>
        <SectionHeading>
          <Text variant="headerXSmall" element="span">
            Loaded modules
          </Text>
        </SectionHeading>
        {moduleRows.length === 0 ? (
          <Text variant="bodySmall">No module versions on this view.</Text>
        ) : (
          <ItemList>
            {moduleRows.map((row) => (
              <LoadedModuleItem key={row.moduleId} row={row} />
            ))}
          </ItemList>
        )}
      </Section>

      <Section>
        <SectionHeading>
          <Text variant="headerXSmall" element="span">
            Shell version override
          </Text>
        </SectionHeading>
        <FieldStack>
          <SelectInput
            id="dev-tools-shell-version"
            label="Shell version"
            value={shellVersionOverride ?? ""}
            onChange={(event) => {
              const value = event.target.value;
              void onApplyMutation(
                putShellVersionOverride(
                  apiBaseUrl,
                  value === "" ? null : value,
                ),
              );
            }}
          >
            <option value="">
              Default ({developerTools.defaultShellVersion})
            </option>
            {developerTools.availableShellVersions.map((version) => (
              <option key={version} value={version}>
                Shell {version}
              </option>
            ))}
          </SelectInput>
        </FieldStack>
        <SectionCopy>
          <Text variant="bodySmall">
            Changing the shell version requires a full page reload and a shell build
            deployed for the target version.
          </Text>
        </SectionCopy>
      </Section>

      <Section>
        <SectionHeading>
          <Text variant="headerXSmall" element="span">
            Manifest version override
          </Text>
        </SectionHeading>
        {manifestVersionOverride ? (
          <Text
            variant="bodySmall"
            element="p"
            data-testid="manifest-version-override-active"
          >
            Active: <Mono>{manifestVersionOverride.branchId}</Mono> @{" "}
            <Mono>{manifestVersionOverride.version}</Mono>
          </Text>
        ) : (
          <Text variant="bodySmall">No manifest version override active.</Text>
        )}
        <SectionCopy>
          <ManifestVersionOverrideForm
            onSubmit={({ branchId, version }) => {
              void onApplyMutation(
                putManifestVersionOverride(apiBaseUrl, branchId, version),
              );
            }}
          />
        </SectionCopy>
        {manifestVersionOverride ? (
          <ButtonRow>
            <Button
              size="small"
              variant="secondary"
              type="button"
              onClick={() => {
                void onApplyMutation(
                  putManifestVersionOverride(
                    apiBaseUrl,
                    manifestVersionOverride.branchId,
                    null,
                  ),
                );
              }}
            >
              Clear manifest version override
            </Button>
          </ButtonRow>
        ) : null}
      </Section>

      <Section>
        <SectionHeading>
          <Text variant="headerXSmall" element="span">
            Bundle overrides
          </Text>
        </SectionHeading>
        {isLoading ? (
          <Text variant="bodySmall">Loading override state…</Text>
        ) : null}
        {snapshot ? (
          <>
            <BundleOverrideList
              snapshot={snapshot}
              onRemove={(kind, id) => {
                void onApplyMutation(deleteBundleOverride(apiBaseUrl, kind, id));
              }}
            />
            <ButtonRow>
              <Button
                size="small"
                variant="secondary"
                type="button"
                onClick={() => {
                  void onApplyMutation(
                    clearAllOverrides(apiBaseUrl, manifestVersionOverride),
                  );
                }}
              >
                Clear all overrides
              </Button>
            </ButtonRow>
          </>
        ) : null}
        <FormDivider>
          <Text variant="labelMedium" element="p">
            Add override
          </Text>
          <AddOverrideForm
            onSubmit={({ kind, id, bundleUrl, manifestUrl }) => {
              void onApplyMutation(
                putBundleOverride(apiBaseUrl, {
                  kind,
                  id,
                  bundleUrl,
                  ...(kind === "view" && manifestUrl !== ""
                    ? { manifestUrl }
                    : {}),
                }),
              );
            }}
          />
        </FormDivider>
      </Section>

      {statusMessage ? (
        <Banner variant={pendingReload ? "warning" : "success"} compact showIcon>
          {statusMessage}
          {pendingReload ? (
            <ButtonRow>
              <Button size="small" type="button" onClick={onReloadPage}>
                Reload now
              </Button>
            </ButtonRow>
          ) : null}
        </Banner>
      ) : null}
      {errorMessage ? (
        <Banner variant="error" compact showIcon>
          {errorMessage}
        </Banner>
      ) : null}
    </>
  );
}

/**
 * In-shell developer tools for inspecting loaded versions and managing dev
 * overrides in non-production environments.
 */
export function DeveloperToolsPane() {
  const { apiBaseUrl, developerTools } = useShellRuntime();
  const {
    isOpen,
    toggle,
    statusMessage,
    pendingReload,
    noteMutationResult,
    reloadPage,
  } = useDevToolsPanelState();

  const [snapshot, setSnapshot] = useState<DevToolsSnapshot | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const refreshSnapshot = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const next = await fetchDevToolsSnapshot(apiBaseUrl);
      setSnapshot(next);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to load dev tools",
      );
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      void refreshSnapshot();
    }
    toggle();
  }, [isOpen, refreshSnapshot, toggle]);

  if (developerTools === undefined) {
    return null;
  }

  const hasActiveOverride = snapshot
    ? hasBundleOverrideEntries(snapshot.bundleOverrides) ||
      snapshot.manifestVersionOverride !== null
    : hasBundleOverrideEntries(developerTools.devOverrides) ||
      developerTools.manifestVersionOverride !== null;

  const applyMutation = async (
    promise: Promise<{ message: string; reloadRequired: boolean }>,
  ) => {
    setErrorMessage(null);
    try {
      const result = await promise;
      noteMutationResult(result.message, result.reloadRequired);
      await refreshSnapshot();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Dev tools request failed",
      );
    }
  };

  return (
    <ToggleWrap data-testid="dev-tools-host" data-chromatic="ignore">
      <ToggleButtonWrap>
        <Button
          type="button"
          size="small"
          variant="secondary"
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-label={
            hasActiveOverride ? "Dev tools (override active)" : "Dev tools"
          }
          data-testid="dev-tools-toggle"
        >
          Dev tools
        </Button>
        {hasActiveOverride ? (
          <OverrideIndicatorDot
            data-testid="dev-tools-override-indicator"
            aria-hidden
          />
        ) : null}
      </ToggleButtonWrap>
      <Drawer.Root
        open={isOpen}
        onClose={handleToggle}
        placement="right"
        desktopSize={480}
        onOverlayClose
      >
        <Drawer.Header>Tinkertoy developer tools</Drawer.Header>
        <Drawer.Body>
          {isOpen ? (
            <DeveloperToolsDrawerBody
              apiBaseUrl={apiBaseUrl}
              developerTools={developerTools}
              snapshot={snapshot}
              isLoading={isLoading}
              errorMessage={errorMessage}
              statusMessage={statusMessage}
              pendingReload={pendingReload}
              onReloadPage={reloadPage}
              onApplyMutation={applyMutation}
            />
          ) : null}
        </Drawer.Body>
      </Drawer.Root>
    </ToggleWrap>
  );
}
